'use client';

import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { ClientCookiesStore } from '@/services/cookies/client';
import {
  GuestBookContent,
  ReservationWithRateDetailsDTO,
  CheckInSettings,
} from '@/models';

interface PreviewData {
  content?: GuestBookContent | null;
  reservation?: ReservationWithRateDetailsDTO | null;
  checkInSettings?: CheckInSettings | null;
}

interface PreviewContextType {
  previewData: PreviewData;
  updatePreviewData: (data: Partial<PreviewData>) => void;
}

const PreviewContext = createContext<PreviewContextType | null>(null);

export const usePreviewState = () => {
  const context = useContext(PreviewContext);
  return context;
};

interface PreviewStateProviderProps {
  children: React.ReactNode;
  initialData?: PreviewData;
  guestBookSlug?: string;
  pageSlug?: string;
}

const getStorageKey = (guestBookSlug?: string, pageSlug?: string): string => {
  const base = 'whistle_preview_data';
  const parts = [base, guestBookSlug, pageSlug].filter(Boolean);

  if (parts.length < 2 && typeof window !== 'undefined') {
    const pathKey = window.location.pathname.replace(/\//g, '_');
    if (pathKey) parts.push(pathKey);
  }

  return parts.join('_');
};

const clearStoredPreviewData = (
  guestBookSlug?: string,
  pageSlug?: string,
): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(getStorageKey(guestBookSlug, pageSlug));
  } catch (error) {
    console.error('Failed to clear stored preview data:', error);
  }
};

const getStoredPreviewData = (
  guestBookSlug?: string,
  pageSlug?: string,
): PreviewData => {
  if (typeof window === 'undefined') return {};

  try {
    const key = getStorageKey(guestBookSlug, pageSlug);
    const stored = sessionStorage.getItem(key);
    if (stored) {
      const parsed: PreviewData = JSON.parse(stored);
      // Self-heal: if a previous bug persisted another page's content under
      // this key, drop it instead of hydrating the wrong content.
      if (
        pageSlug &&
        parsed.content?.slug &&
        parsed.content.slug !== pageSlug
      ) {
        clearStoredPreviewData(guestBookSlug, pageSlug);
        return {};
      }
      return parsed;
    }
  } catch (error) {
    console.error('Failed to parse stored preview data:', error);
  }

  return {};
};

const storePreviewData = (
  data: PreviewData,
  guestBookSlug?: string,
  pageSlug?: string,
): void => {
  if (typeof window === 'undefined') return;

  try {
    const key = getStorageKey(guestBookSlug, pageSlug);
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to store preview data:', error);
  }
};

/**
 * Context provider that manages preview state and listens for preview updates
 * Provides real-time preview data to child components via React Context
 */
export const PreviewStateProvider = ({
  children,
  initialData = {},
  guestBookSlug,
  pageSlug,
}: PreviewStateProviderProps) => {
  const [previewData, setPreviewData] = useState<PreviewData>(initialData);

  const updatePreviewData = useCallback(
    (data: Partial<PreviewData>) => {
      setPreviewData((prev) => {
        const updated = { ...prev, ...data };
        storePreviewData(updated, guestBookSlug, pageSlug);
        return updated;
      });
    },
    [guestBookSlug, pageSlug],
  );

  useEffect(() => {
    const stored = getStoredPreviewData(guestBookSlug, pageSlug);
    if (Object.keys(stored).length > 0) {
      setPreviewData(stored);
    }
  }, [guestBookSlug, pageSlug]);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      if (e.data && e.data.type) {
        switch (e.data.type) {
          case 'UPDATE_PAGE_CONTENT': {
            const incomingSlug: string | undefined = e.data.content?.slug;
            if (pageSlug && incomingSlug && incomingSlug !== pageSlug) {
              return;
            }
            updatePreviewData({ content: e.data.content });
            break;
          }
          case 'UPDATE_PREVIEW_RESERVATION': {
            updatePreviewData({ reservation: e.data.reservation });
            break;
          }
          case 'UPDATE_CHECK_IN_SETTINGS': {
            updatePreviewData({ checkInSettings: e.data.checkInSettings });
            break;
          }
        }
      }
    };

    // Set preview cookie
    ClientCookiesStore.setCookie('whistle-guestbook', 'true');

    // Notify parent that preview is loaded
    window.parent?.postMessage({ type: 'PREVIEW_LOADED' }, '*');

    // Add message listener
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [updatePreviewData, pageSlug]);

  return (
    <PreviewContext.Provider value={{ previewData, updatePreviewData }}>
      {children}
    </PreviewContext.Provider>
  );
};
